// React
import React, {
  useState, useRef, useEffect, useContext,
} from 'react';

// Components from libraries
import { Modal } from 'anf-core-react';

// Local Components
import ProductMainImages from './ProductMainImages';
import ImageGalleryGrid from './ImageGalleryGrid';
import MobileImageSlider from './MobileImageSlider';
import ZoomImages from './ZoomImages';

// tools
import BrowserContext from '../../contexts/BrowserContext';
import useImageManager from './useImageManager';
import ProductPageContext from '../../contexts/ProductPageContext';
import ImagePlaceholder from './ImagePlaceholder';
import AltImageSkeletonLoading from './AltImageSkeletonLoading';

export default function MainImages({
  isMobile,
}) {
  const ProductPageState = useContext(ProductPageContext);
  const {
    tmntData, collectionData,
  } = ProductPageState;
  const isBrowser = useContext(BrowserContext);

  const { activeProduct } = collectionData;

  const {
    imageData,
    setImageData,
    handleImageErrorOrRemoval,
  } = useImageManager([], activeProduct?.brand);

  const [activeImage, setActiveImage] = useState({});
  const [openZoom, setOpenZoom] = useState(false);
  const mobileRef = useRef(null);

  useEffect(() => {
    if (activeProduct) {
      setImageData(activeProduct.orderedImages);
      setActiveImage(activeProduct.orderedImages[0]);
      mobileRef.current?.clearMobileImageLoaderCount();
    }
  }, [activeProduct, setImageData]);

  const activeBrand = activeProduct?.brand;
  const showGallery = imageData?.length > 2;

  if (!activeProduct) { return null; }

  const onImageClick = (event, imageDetails) => {
    setActiveImage({ ...imageDetails, target: event.target });
    setOpenZoom(true);
  };

  const onModalClose = () => {
    setOpenZoom(false);
  };
  const componentImageSwap = () => {
    if (showGallery) {
      return (
        <ImageGalleryGrid
          productImages={imageData}
          onClick={(event, img) => onImageClick(event, img)}
          handleImageErrorOrRemoval={handleImageErrorOrRemoval}
          tmntData={tmntData}
          singleView={!showGallery}
          isMobile={isMobile}
        />
      );
    }
    return (
      <ProductMainImages
        productImages={imageData}
        onClick={(event, img) => onImageClick(event, img)}
        handleImageErrorOrRemoval={handleImageErrorOrRemoval}
        tmntData={tmntData}
        singleView={!showGallery}
        isMobile={isMobile}
      />
    );
  };

  const renderMobileImageSlider = () => (
    <MobileImageSlider
      productImages={imageData}
      ref={mobileRef}
      handleImageErrorOrRemoval={handleImageErrorOrRemoval}
      tmntData={tmntData}
      onClick={(event, img) => onImageClick(event, img)}
    />
  );

  const getMobileLoadingComponent = () => (
    collectionData?.hasMobileAltImageThumbnail
      ? (
        <AltImageSkeletonLoading
          hasFitInfo={collectionData?.activeProduct?.hasFitInfo}
          totalImages={collectionData.activeProduct.orderedImages.length}
          isBrowser={isBrowser}
        />
      )
      : <ImagePlaceholder singleView isMobile />
  );

  const renderMobileContent = () => {
    if (collectionData?.hasMobileAltImageThumbnail) {
      return null;
    }
    return renderMobileImageSlider();
  };

  return (
    <section className="product-page-images-mfe" data-testid="product-image-section" data-product-brand={activeBrand}>
      <div className="mobile-view">
        {!isBrowser && getMobileLoadingComponent()}
      </div>
      {isMobile ? renderMobileContent() : componentImageSwap()}
      <Modal
        isOpen={openZoom}
        isFullBleed
        id="product-zoom__modal"
        data-testid="product-zoom__modal"
        isFullscreen
        isSticky
        variant="headerless"
        onClose={onModalClose}
      >
        <ZoomImages
          orderedImages={imageData}
          productId={activeProduct.productId}
          activeImage={activeImage}
          setActiveImage={setActiveImage}
          tmntData={tmntData}
          handleImageErrorOrRemoval={handleImageErrorOrRemoval}
        />
      </Modal>
    </section>
  );
}
